export interface HosUtilizationDay {
  market: string;
  driver_number?: string | null;
  driver_name?: string | null;
  duty_date?: string;
  carried_over?: boolean;
  off_duty_at?: string | null;
  on_duty_span_minutes?: number | null;
  first_on_duty_at: string | null;
  driving_minutes: number | null;
  on_duty_minutes: number | null;
}

const wrapMinutes = (minutes: number) => ((minutes % 1440) + 1440) % 1440;

function summarizeTimes(days: HosUtilizationDay[], kind: 'start' | 'end') {
  const starts = days.flatMap(day => {
    if (day.carried_over || !day.first_on_duty_at) return [];
    const timestamp = kind === 'start' ? day.first_on_duty_at : day.off_duty_at;
    if (!timestamp || (kind === 'end' && timestamp <= day.first_on_duty_at)) return [];
    const match = timestamp.match(/(?:T| )(\d{2}):(\d{2}):(\d{2})/);
    if (!match) return [];
    const minutes = Number(match[1]) * 60 + Number(match[2]) + Number(match[3]) / 60;
    return minutes >= 0 && minutes < 1440 ? [{ day, minutes }] : [];
  });
  // Unwrap around the circular center before taking the arithmetic mean and
  // population deviation, so 23:00 and 01:00 average to midnight, not noon.
  const angle = (minutes: number) => minutes * 2 * Math.PI / 1440;
  const sin = starts.reduce((sum, row) => sum + Math.sin(angle(row.minutes)), 0);
  const cos = starts.reduce((sum, row) => sum + Math.cos(angle(row.minutes)), 0);
  const center = Math.atan2(sin, cos) * 1440 / (2 * Math.PI);
  const values = starts.map(row => ({ ...row, value: center + wrapMinutes(row.minutes - center + 720) - 720 }));
  const defined = starts.length > 0 && Math.hypot(sin, cos) / starts.length > 1e-6;
  const mean = defined ? values.reduce((sum, row) => sum + row.value, 0) / values.length : null;
  const deviation = mean === null ? null : Math.sqrt(values.reduce((sum, row) => sum + (row.value - mean) ** 2, 0) / values.length);
  const spans = starts.map(row => row.day.on_duty_span_minutes).filter((span): span is number => span != null && Number.isFinite(span) && span > 0);
  return {
    averageMinutes: mean === null ? null : wrapMinutes(mean),
    stddevMinutes: deviation,
    count: starts.length,
    averageWorkingMinutes: spans.length ? spans.reduce((sum, span) => sum + span, 0) / spans.length : null,
    outliers: mean === null || deviation === null ? [] : values
      .filter(row => Math.abs(row.value - mean) > 2 * deviation + 1e-8)
      .sort((a, b) => a.value - b.value)
      .map(row => ({ driver_number: row.day.driver_number, driver_name: row.day.driver_name,
        duty_date: kind === 'end' ? row.day.off_duty_at?.slice(0, 10) : row.day.duty_date, minutes: row.minutes, direction: row.value < mean ? 'Early' : 'Late' })),
  };
}

export function formatHosStart(minutes: number | null | undefined): string {
  if (minutes == null) return 'Unavailable';
  const rounded = wrapMinutes(Math.round(minutes));
  const hour = Math.floor(rounded / 60);
  return `${hour % 12 || 12}:${String(rounded % 60).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
}

function summarize(market: string, days: HosUtilizationDay[]) {
  const worked = days.filter(day => day.first_on_duty_at || (day.on_duty_minutes ?? 0) > 0);
  const driving = worked.filter(day => day.driving_minutes != null);
  const onDuty = worked.filter(day => day.on_duty_minutes != null);
  const drivingMinutes = driving.reduce((sum, day) => sum + day.driving_minutes!, 0);
  const onDutyMinutes = onDuty.reduce((sum, day) => sum + day.on_duty_minutes!, 0);
  const starts = summarizeTimes(days, 'start');
  const ends = summarizeTimes(days, 'end');
  return {
    averageStartMinutes: starts.averageMinutes,
    startStddevMinutes: starts.stddevMinutes,
    startDays: starts.count,
    startOutliers: starts.outliers,
    averageWorkingMinutes: starts.averageWorkingMinutes,
    averageEndMinutes: ends.averageMinutes,
    endStddevMinutes: ends.stddevMinutes,
    endDays: ends.count,
    endOutliers: ends.outliers,
    market, workedDays: worked.length,
    drivingDays: driving.length, onDutyDays: onDuty.length,
    drivingMinutes, onDutyMinutes,
    averageDrivingMinutes: driving.length ? drivingMinutes / driving.length : null,
    averageOnDutyMinutes: onDuty.length ? onDutyMinutes / onDuty.length : null,
    drivingPercent: driving.length ? drivingMinutes / (driving.length * 660) * 100 : null,
    onDutyPercent: onDuty.length ? onDutyMinutes / (onDuty.length * 840) * 100 : null,
    missingDays: worked.filter(day => day.driving_minutes == null || day.on_duty_minutes == null).length,
  };
}

export function summarizeHosMarkets(rows: HosUtilizationDay[]) {
  const markets = [...new Set(rows.map(row => row.market || 'Unassigned'))].sort();
  return [summarize('All markets', rows), ...markets.map(market =>
    summarize(market, rows.filter(row => (row.market || 'Unassigned') === market)))];
}

interface HosDriverDay extends HosUtilizationDay {
  samsara_driver_id: string;
  driver_name: string | null;
  isMule?: boolean;
}

export function summarizeHosDrivers(rows: HosDriverDay[]) {
  const drivers = new Map<string, HosDriverDay[]>();
  for (const row of rows) {
    const days = drivers.get(row.samsara_driver_id) ?? [];
    days.push(row);
    drivers.set(row.samsara_driver_id, days);
  }
  return [...drivers.values()].map(days => ({
    ...summarize(days[0].market, days),
    samsara_driver_id: days[0].samsara_driver_id,
    driver_name: days.find(day => day.driver_name)?.driver_name ?? days[0].samsara_driver_id,
    isMule: days.some(day => day.isMule),
  })).filter(driver => driver.workedDays > 0);
}

export type HosDriverSummary = ReturnType<typeof summarizeHosDrivers>[number];

/** The input is ordered earliest to latest around the summary's clock center. */
export function selectHosOutlierExtremes(outliers: HosDriverSummary['startOutliers']) {
  return [
    ...outliers.filter(row => row.direction === 'Early').slice(0, 3),
    ...outliers.filter(row => row.direction === 'Late').slice(-3),
  ];
}
